import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { 
  TrendingUp, 
  TrendingDown, 
  Calendar,
  Newspaper,
  RefreshCw,
  Globe,
  Factory,
  Wheat,
  Flame,
  Gem,
  Clock,
  ExternalLink,
  Activity
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Treemap, ResponsiveContainer, Tooltip } from 'recharts';
import axios from 'axios';

const FINNHUB_API_KEY = 'd5t7b2hr01qt62nhn9t0d5t7b2hr01qt62nhn9tg';
const FINNHUB_BASE = 'https://finnhub.io/api/v1';

// Initial mock prices to show immediately (will be replaced with live data)
const INITIAL_PRICES = {
  'WEAT': { price: 22.45, changePercent: 1.2, high: 22.80, low: 22.10, open: 22.20 },
  'CORN': { price: 18.32, changePercent: -0.8, high: 18.60, low: 18.20, open: 18.45 },
  'SOYB': { price: 24.78, changePercent: 0.5, high: 25.00, low: 24.50, open: 24.65 },
  'CANE': { price: 9.45, changePercent: 2.1, high: 9.60, low: 9.30, open: 9.35 },
  'JO': { price: 67.23, changePercent: -1.5, high: 68.00, low: 66.80, open: 67.80 },
  'NIB': { price: 45.67, changePercent: 0.3, high: 46.00, low: 45.20, open: 45.50 },
  'USO': { price: 78.90, changePercent: -2.3, high: 80.50, low: 78.50, open: 80.10 },
  'UNG': { price: 12.34, changePercent: 3.5, high: 12.60, low: 12.00, open: 12.10 },
  'UGA': { price: 56.78, changePercent: -1.2, high: 57.50, low: 56.40, open: 57.20 },
  'BNO': { price: 32.45, changePercent: -1.8, high: 33.20, low: 32.30, open: 33.00 },
  'GLD': { price: 178.90, changePercent: 0.8, high: 179.50, low: 178.00, open: 178.20 },
  'SLV': { price: 23.45, changePercent: 1.5, high: 23.80, low: 23.20, open: 23.30 },
  'CPER': { price: 24.56, changePercent: -0.5, high: 24.80, low: 24.40, open: 24.65 },
  'PPLT': { price: 89.23, changePercent: 2.0, high: 90.00, low: 88.50, open: 88.80 },
  'PALL': { price: 145.67, changePercent: 1.2, high: 146.50, low: 144.80, open: 145.00 },
  'DBA': { price: 21.34, changePercent: 0.6, high: 21.50, low: 21.10, open: 21.20 },
  'DBC': { price: 23.78, changePercent: -0.9, high: 24.10, low: 23.60, open: 24.00 },
  'GSG': { price: 19.45, changePercent: -1.1, high: 19.80, low: 19.30, open: 19.70 },
  'PDBC': { price: 14.23, changePercent: 0.4, high: 14.40, low: 14.10, open: 14.15 },
};

// Commodity symbols for futures/ETFs
const COMMODITY_CATEGORIES = {
  'Grains & Agriculture': {
    icon: Wheat,
    color: '#c5a059',
    items: [
      { symbol: 'WEAT', name: 'Wheat', displaySymbol: 'WHEAT' },
      { symbol: 'CORN', name: 'Corn', displaySymbol: 'CORN' },
      { symbol: 'SOYB', name: 'Soybeans', displaySymbol: 'SOYB' },
      { symbol: 'CANE', name: 'Sugar', displaySymbol: 'SUGR' },
      { symbol: 'JO', name: 'Coffee', displaySymbol: 'COFF' },
      { symbol: 'NIB', name: 'Cocoa', displaySymbol: 'COCO' },
    ]
  },
  'Energy': {
    icon: Flame,
    color: '#ef4444',
    items: [
      { symbol: 'USO', name: 'Crude Oil', displaySymbol: 'OIL' },
      { symbol: 'UNG', name: 'Natural Gas', displaySymbol: 'NGAS' },
      { symbol: 'UGA', name: 'Gasoline', displaySymbol: 'GAS' },
      { symbol: 'BNO', name: 'Brent Crude', displaySymbol: 'BRENT' },
    ]
  },
  'Metals': {
    icon: Gem,
    color: '#3b82f6',
    items: [
      { symbol: 'GLD', name: 'Gold', displaySymbol: 'GOLD' },
      { symbol: 'SLV', name: 'Silver', displaySymbol: 'SLVR' },
      { symbol: 'CPER', name: 'Copper', displaySymbol: 'COPR' },
      { symbol: 'PPLT', name: 'Platinum', displaySymbol: 'PLAT' },
      { symbol: 'PALL', name: 'Palladium', displaySymbol: 'PALL' },
    ]
  },
  'Industrial': {
    icon: Factory,
    color: '#8b5cf6',
    items: [
      { symbol: 'PDBC', name: 'Diversified Cmdty', displaySymbol: 'CMDY' },
      { symbol: 'DBA', name: 'Agriculture', displaySymbol: 'AGRI' },
      { symbol: 'DBB', name: 'Base Metals', displaySymbol: 'BASE' },
      { symbol: 'DBC', name: 'Commodity Index', displaySymbol: 'INDX' },
    ]
  }
};

// Flatten for processing
const ALL_COMMODITIES = Object.entries(COMMODITY_CATEGORIES).flatMap(([category, data]) => 
  data.items.map(item => ({ ...item, category, categoryColor: data.color }))
);

// Get treemap cell color based on percentage change - Finviz style gradients
const getTreemapColor = (changePercent) => {
  if (changePercent >= 3) return '#00c853';       // Bright green
  if (changePercent >= 2) return '#00a844';
  if (changePercent >= 1) return '#008c36';
  if (changePercent >= 0.5) return '#007028';
  if (changePercent > 0) return '#0d5a1d';        // Dark green
  if (changePercent === 0) return '#374151';      // Gray
  if (changePercent > -0.5) return '#5a1d1d';     // Dark red
  if (changePercent > -1) return '#702828';
  if (changePercent > -2) return '#8c3636';
  if (changePercent > -3) return '#a84444';
  return '#c85353';                                // Bright red
};

// Custom treemap content component for Finviz-style display
const CustomTreemapContent = (props) => {
  const { x, y, width, height, name, displaySymbol, changePercent, price, category, depth } = props;
  
  // Skip rendering for invalid dimensions only
  if (!width || !height || width < 15 || height < 15) return null;
  
  const bgColor = getTreemapColor(changePercent || 0);
  const isSmall = width < 80 || height < 60;
  const isTiny = width < 50 || height < 40;
  
  // Use displaySymbol if available, fallback to name
  const label = displaySymbol || name || '';
  
  if (isTiny) {
    return (
      <g>
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          fill={bgColor}
          stroke="#0a1628"
          strokeWidth={2}
        />
        <text
          x={x + width / 2}
          y={y + height / 2}
          textAnchor="middle"
          dominantBaseline="middle"
          fill="white"
          fontSize={9}
          fontWeight="bold"
        >
          {label.substring(0, 4)}
        </text>
      </g>
    );
  }
  
  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill={bgColor}
        stroke="#0a1628"
        strokeWidth={2}
        rx={2}
      />
      
      {/* Symbol */}
      <text
        x={x + width / 2}
        y={y + (isSmall ? height * 0.35 : height * 0.3)}
        textAnchor="middle"
        dominantBaseline="middle"
        fill="white"
        fontSize={isSmall ? 12 : 16}
        fontWeight="bold"
        fontFamily="JetBrains Mono, monospace"
      >
        {label}
      </text>
      
      {/* Percentage Change */}
      <text
        x={x + width / 2}
        y={y + (isSmall ? height * 0.65 : height * 0.55)}
        textAnchor="middle"
        dominantBaseline="middle"
        fill="white"
        fontSize={isSmall ? 11 : 14}
        fontWeight="bold"
        fontFamily="JetBrains Mono, monospace"
      >
        {changePercent !== undefined && changePercent !== null ? `${changePercent >= 0 ? '+' : ''}${changePercent.toFixed(2)}%` : '--'}
      </text>
      
      {/* Price - only show if not small */}
      {!isSmall && price !== undefined && price !== null && (
        <text
          x={x + width / 2}
          y={y + height * 0.75}
          textAnchor="middle"
          dominantBaseline="middle"
          fill="rgba(255,255,255,0.7)"
          fontSize={11}
          fontFamily="JetBrains Mono, monospace"
        >
          ${price.toFixed(2)}
        </text>
      )}
      
      {/* Category label - only for larger tiles */}
      {!isSmall && height > 90 && category && (
        <text
          x={x + width / 2}
          y={y + height * 0.9}
          textAnchor="middle"
          dominantBaseline="middle"
          fill="rgba(255,255,255,0.4)"
          fontSize={9}
          fontFamily="Inter, sans-serif"
        >
          {category.split(' ')[0]}
        </text>
      )}
    </g>
  );
};

// Custom tooltip for treemap
const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  
  const data = payload[0].payload;
  const changePercent = data.changePercent || 0;
  
  return (
    <div className="bg-[#0a1628]/95 backdrop-blur-sm border border-white/20 p-4 shadow-2xl">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-secondary-500 text-xs font-mono uppercase">{data.category}</span>
      </div>
      <h4 className="text-white font-bold text-lg">{data.name}</h4>
      <p className="text-white/60 text-sm mb-3">{data.displaySymbol}</p>
      
      <div className="space-y-1 text-sm border-t border-white/10 pt-3">
        <div className="flex justify-between">
          <span className="text-white/50">Price:</span>
          <span className="text-white font-mono">${data.price?.toFixed(2) || '--'}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-white/50">Change:</span>
          <span className={`font-mono font-bold ${changePercent >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {changePercent >= 0 ? '+' : ''}{changePercent.toFixed(2)}%
          </span>
        </div>
        {data.high && (
          <div className="flex justify-between">
            <span className="text-white/50">High:</span>
            <span className="text-green-400 font-mono">${data.high.toFixed(2)}</span>
          </div>
        )}
        {data.low && (
          <div className="flex justify-between">
            <span className="text-white/50">Low:</span>
            <span className="text-red-400 font-mono">${data.low.toFixed(2)}</span>
          </div>
        )}
      </div>
    </div>
  );
};

const MarketAnalysis = () => {
  const { t } = useLanguage();
  const [prices, setPrices] = useState(INITIAL_PRICES);
  const [economicEvents, setEconomicEvents] = useState([]);
  const [marketNews, setMarketNews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeView, setActiveView] = useState('heatmap');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [lastUpdate, setLastUpdate] = useState(new Date());

  // Fetch commodity prices
  const fetchPrices = useCallback(async () => {
    const newPrices = {};
    
    for (const commodity of ALL_COMMODITIES) {
      try {
        const response = await axios.get(`${FINNHUB_BASE}/quote`, {
          params: {
            symbol: commodity.symbol,
            token: FINNHUB_API_KEY
          }
        });
        
        if (response.data && response.data.c) {
          const currentPrice = response.data.c;
          const previousClose = response.data.pc;
          const change = currentPrice - previousClose;
          const changePercent = previousClose > 0 ? ((change / previousClose) * 100) : 0;
          
          newPrices[commodity.symbol] = {
            price: currentPrice,
            change,
            changePercent,
            high: response.data.h,
            low: response.data.l,
            open: response.data.o
          };
        }
      } catch (error) {
        console.error(`Failed to fetch ${commodity.symbol}:`, error);
      }
    }
    
    setPrices(newPrices);
    setLastUpdate(new Date());
  }, []);

  // Fetch economic calendar
  const fetchEconomicCalendar = useCallback(async () => {
    try {
      const today = new Date();
      const nextWeek = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
      
      const response = await axios.get(`${FINNHUB_BASE}/calendar/economic`, {
        params: {
          from: today.toISOString().split('T')[0],
          to: nextWeek.toISOString().split('T')[0],
          token: FINNHUB_API_KEY
        }
      });
      
      if (response.data && response.data.economicCalendar) {
        // Filter for commodity-related events
        const commodityKeywords = ['oil', 'gas', 'gold', 'commodity', 'agriculture', 'crop', 'grain', 'metal', 'energy', 'wheat', 'corn', 'copper', 'inventory', 'storage'];
        const filtered = response.data.economicCalendar
          .filter(event => {
            const eventText = `${event.event} ${event.country}`.toLowerCase();
            return commodityKeywords.some(keyword => eventText.includes(keyword)) || 
                   event.impact === 'high';
          })
          .slice(0, 15);
        setEconomicEvents(filtered);
      }
    } catch (error) {
      console.error('Failed to fetch economic calendar:', error);
      // Use sample data when API fails (Finnhub free tier doesn't include economic calendar)
      const today = new Date();
      const sampleEvents = [
        { event: 'EIA Crude Oil Inventories', country: 'US', impact: 'high', date: new Date(today.getTime() + 1 * 24 * 60 * 60 * 1000).toISOString(), estimate: '-1.2M', prev: '-2.5M' },
        { event: 'OPEC Monthly Oil Market Report', country: 'OPEC', impact: 'high', date: new Date(today.getTime() + 2 * 24 * 60 * 60 * 1000).toISOString() },
        { event: 'Natural Gas Storage Change', country: 'US', impact: 'medium', date: new Date(today.getTime() + 2 * 24 * 60 * 60 * 1000).toISOString(), estimate: '+15B', prev: '+12B' },
        { event: 'Gold Fixing Price (AM)', country: 'UK', impact: 'medium', date: new Date(today.getTime() + 3 * 24 * 60 * 60 * 1000).toISOString() },
        { event: 'Copper Futures Settlement', country: 'US', impact: 'medium', date: new Date(today.getTime() + 3 * 24 * 60 * 60 * 1000).toISOString() },
        { event: 'USDA Grain Stocks Report', country: 'US', impact: 'high', date: new Date(today.getTime() + 4 * 24 * 60 * 60 * 1000).toISOString() },
        { event: 'China Manufacturing PMI', country: 'CN', impact: 'high', date: new Date(today.getTime() + 5 * 24 * 60 * 60 * 1000).toISOString(), estimate: '50.2', prev: '49.8' },
        { event: 'Baker Hughes Oil Rig Count', country: 'US', impact: 'medium', date: new Date(today.getTime() + 5 * 24 * 60 * 60 * 1000).toISOString(), prev: '482' },
        { event: 'API Weekly Crude Oil Stock', country: 'US', impact: 'medium', date: new Date(today.getTime() + 6 * 24 * 60 * 60 * 1000).toISOString() },
        { event: 'World Agricultural Supply & Demand', country: 'US', impact: 'high', date: new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000).toISOString() },
      ];
      setEconomicEvents(sampleEvents);
    }
  }, []);

  // Fetch market news
  const fetchMarketNews = useCallback(async () => {
    try {
      const response = await axios.get(`${FINNHUB_BASE}/news`, {
        params: {
          category: 'general',
          token: FINNHUB_API_KEY
        }
      });
      
      if (response.data) {
        // Filter for commodity-related news
        const commodityKeywords = ['oil', 'gas', 'gold', 'silver', 'commodity', 'agriculture', 'wheat', 'corn', 'copper', 'crude', 'metal', 'grain', 'sugar', 'coffee'];
        const filtered = response.data
          .filter(news => {
            const newsText = `${news.headline} ${news.summary}`.toLowerCase();
            return commodityKeywords.some(keyword => newsText.includes(keyword));
          })
          .slice(0, 10);
        setMarketNews(filtered.length > 0 ? filtered : response.data.slice(0, 8));
      }
    } catch (error) {
      console.error('Failed to fetch market news:', error);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    const fetchAllData = async () => {
      // Fetch data in background - don't show loading since we have initial data
      await Promise.all([
        fetchPrices(),
        fetchEconomicCalendar(),
        fetchMarketNews()
      ]);
    };
    
    fetchAllData();
    
    // Refresh prices every 60 seconds
    const interval = setInterval(fetchPrices, 60000);
    return () => clearInterval(interval);
  }, [fetchPrices, fetchEconomicCalendar, fetchMarketNews]);

  // Filter commodities by category
  const filteredCommodities = selectedCategory === 'all' 
    ? ALL_COMMODITIES 
    : ALL_COMMODITIES.filter(c => c.category === selectedCategory);

  // Prepare treemap data - sizes based on absolute percentage change (bigger moves = bigger tiles)
  const treemapData = useMemo(() => {
    return filteredCommodities.map(commodity => {
      const priceData = prices[commodity.symbol];
      const changePercent = priceData?.changePercent || 0;
      // Size based on absolute change (bigger moves get bigger tiles) + base size
      // Min size of 50 so even flat commodities are visible
      const size = Math.max(50, Math.abs(changePercent) * 100 + 30);
      
      return {
        name: commodity.name,
        displaySymbol: commodity.displaySymbol,
        symbol: commodity.symbol,
        category: commodity.category,
        size,
        price: priceData?.price,
        changePercent,
        high: priceData?.high,
        low: priceData?.low,
        open: priceData?.open
      };
    });
  }, [filteredCommodities, prices]);

  return (
    <div className="min-h-screen bg-[#0a1628]" data-testid="market-page">
      {/* Compact Header */}
      <section className="bg-[#0a1628] pt-16 sm:pt-20 border-b border-white/10">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 md:px-10 lg:px-16 py-4 sm:py-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-secondary-500/20 flex items-center justify-center">
                <Activity className="w-5 h-5 text-secondary-500" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-heading font-bold text-white tracking-tight">
                  {t('markets.title')}
                </h1>
                <p className="text-white/40 text-xs sm:text-sm">
                  {t('markets.subtitle')}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {lastUpdate && (
                <span className="text-[10px] font-mono text-white/30 flex items-center gap-1.5 bg-white/5 px-2 py-1">
                  <Clock className="w-3 h-3" />
                  {lastUpdate.toLocaleTimeString()}
                </span>
              )}
              <Button
                onClick={() => {
                  setLoading(true);
                  fetchPrices().then(() => setLoading(false));
                }}
                disabled={loading}
                size="sm"
                className="bg-secondary-500 hover:bg-secondary-400 text-primary-900 rounded-none px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider"
              >
                <RefreshCw className={`w-3 h-3 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
                {t('markets.refresh')}
              </Button>
            </div>
          </div>

          {/* View Tabs - More Compact */}
          <div className="flex gap-1 mt-4 overflow-x-auto">
            {[
              { id: 'heatmap', label: t('markets.tab_treemap'), icon: Globe },
              { id: 'calendar', label: t('markets.tab_calendar'), icon: Calendar },
              { id: 'news', label: t('markets.tab_news'), icon: Newspaper }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveView(tab.id)}
                data-testid={`tab-${tab.id}`}
                className={`flex items-center gap-1.5 px-3 py-2 text-[10px] font-bold uppercase tracking-wider whitespace-nowrap transition-all ${
                  activeView === tab.id
                    ? 'bg-secondary-500 text-primary-900'
                    : 'bg-white/5 text-white/50 hover:bg-white/10 hover:text-white'
                }`}
              >
                <tab.icon className="w-3.5 h-3.5" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Content */}
      <div className="max-w-[1800px] mx-auto px-4 sm:px-6 md:px-10 lg:px-16 py-6">
        <AnimatePresence mode="wait">
          {/* Treemap View */}
          {activeView === 'heatmap' && (
            <motion.div
              key="heatmap"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              {/* Category Filter - More Compact */}
              <div className="flex flex-wrap gap-1.5 mb-4">
                <button
                  onClick={() => setSelectedCategory('all')}
                  data-testid="filter-all"
                  className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider transition-all ${
                    selectedCategory === 'all'
                      ? 'bg-white text-primary-900'
                      : 'bg-white/5 text-white/50 hover:bg-white/10'
                  }`}
                >
                  {t('markets.filter_all')}
                </button>
                {Object.entries(COMMODITY_CATEGORIES).map(([category, data]) => {
                  const Icon = data.icon;
                  return (
                    <button
                      key={category}
                      onClick={() => setSelectedCategory(category)}
                      data-testid={`filter-${category.toLowerCase().replace(/\s+/g, '-')}`}
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider transition-all ${
                        selectedCategory === category
                          ? 'bg-white text-primary-900'
                          : 'bg-white/5 text-white/50 hover:bg-white/10'
                      }`}
                    >
                      <Icon className="w-3 h-3" style={{ color: selectedCategory === category ? '#0a1628' : data.color }} />
                      {category.split(' ')[0]}
                    </button>
                  );
                })}
              </div>

              {/* Dynamic Treemap */}
              {loading ? (
                <div className="h-[500px] sm:h-[600px] bg-white/5 animate-pulse flex items-center justify-center">
                  <div className="text-center">
                    <RefreshCw className="w-8 h-8 text-secondary-500 animate-spin mx-auto mb-3" />
                    <p className="text-white/40 text-sm">{t('markets.loading')}</p>
                  </div>
                </div>
              ) : (
                <div className="bg-[#0d1f35] border border-white/10 p-2" data-testid="commodity-treemap">
                  {/* Custom CSS-based Treemap with squarify layout */}
                  <div 
                    className="relative w-full" 
                    style={{ height: '550px' }}
                  >
                    {(() => {
                      // Squarify algorithm for better treemap layout
                      const squarify = (items, container) => {
                        const { x, y, width, height } = container;
                        const totalSize = items.reduce((sum, d) => sum + d.size, 0);
                        
                        if (items.length === 0) return [];
                        if (items.length === 1) {
                          return [{ ...items[0], x, y, width, height }];
                        }
                        
                        const layouts = [];
                        let remainingItems = [...items].sort((a, b) => b.size - a.size);
                        let remainingArea = { x, y, width, height };
                        let remainingTotal = totalSize;
                        
                        while (remainingItems.length > 0) {
                          const isWide = remainingArea.width >= remainingArea.height;
                          const side = isWide ? remainingArea.height : remainingArea.width;
                          
                          // Find best row/column
                          let row = [];
                          let rowSize = 0;
                          let bestRatio = Infinity;
                          
                          for (let i = 0; i < remainingItems.length; i++) {
                            const testRow = remainingItems.slice(0, i + 1);
                            const testSize = testRow.reduce((sum, d) => sum + d.size, 0);
                            const testWidth = (testSize / remainingTotal) * (isWide ? remainingArea.width : remainingArea.height);
                            
                            let worstRatio = 0;
                            testRow.forEach(item => {
                              const itemHeight = (item.size / testSize) * side;
                              const ratio = Math.max(testWidth / itemHeight, itemHeight / testWidth);
                              worstRatio = Math.max(worstRatio, ratio);
                            });
                            
                            if (worstRatio <= bestRatio) {
                              bestRatio = worstRatio;
                              row = testRow;
                              rowSize = testSize;
                            } else {
                              break;
                            }
                          }
                          
                          // Layout the row
                          const rowWidth = (rowSize / remainingTotal) * (isWide ? remainingArea.width : remainingArea.height);
                          let pos = 0;
                          
                          row.forEach(item => {
                            const itemSize = (item.size / rowSize) * side;
                            if (isWide) {
                              layouts.push({
                                ...item,
                                x: remainingArea.x,
                                y: remainingArea.y + pos,
                                width: rowWidth,
                                height: itemSize
                              });
                            } else {
                              layouts.push({
                                ...item,
                                x: remainingArea.x + pos,
                                y: remainingArea.y,
                                width: itemSize,
                                height: rowWidth
                              });
                            }
                            pos += itemSize;
                          });
                          
                          // Update remaining area
                          if (isWide) {
                            remainingArea = {
                              x: remainingArea.x + rowWidth,
                              y: remainingArea.y,
                              width: remainingArea.width - rowWidth,
                              height: remainingArea.height
                            };
                          } else {
                            remainingArea = {
                              x: remainingArea.x,
                              y: remainingArea.y + rowWidth,
                              width: remainingArea.width,
                              height: remainingArea.height - rowWidth
                            };
                          }
                          
                          remainingItems = remainingItems.slice(row.length);
                          remainingTotal -= rowSize;
                        }
                        
                        return layouts;
                      };
                      
                      const layouts = squarify(treemapData, { x: 0, y: 0, width: 100, height: 100 });
                      
                      return layouts.map((item, idx) => {
                        const bgColor = getTreemapColor(item.changePercent || 0);
                        const isSmall = item.width < 8 || item.height < 10;
                        const isTiny = item.width < 5 || item.height < 6;
                        
                        return (
                          <div
                            key={item.symbol || idx}
                            className="absolute flex flex-col items-center justify-center transition-all duration-200 cursor-pointer hover:brightness-125 hover:z-10 group"
                            style={{
                              left: `${item.x}%`,
                              top: `${item.y}%`,
                              width: `${item.width}%`,
                              height: `${item.height}%`,
                              backgroundColor: bgColor,
                              border: '2px solid #0a1628'
                            }}
                            title={`${item.name}: ${item.changePercent?.toFixed(2) || 0}%`}
                            data-testid={`treemap-tile-${item.symbol}`}
                          >
                            {!isTiny && (
                              <>
                                <span className="text-white font-bold text-xs sm:text-sm lg:text-base font-mono truncate px-1 text-center" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
                                  {item.displaySymbol}
                                </span>
                                <span className="font-bold text-[10px] sm:text-xs lg:text-sm font-mono" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
                                  {item.changePercent !== undefined ? `${item.changePercent >= 0 ? '+' : ''}${item.changePercent.toFixed(2)}%` : '--'}
                                </span>
                                {!isSmall && item.price && (
                                  <span className="text-white/70 text-[8px] sm:text-[10px] font-mono" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
                                    ${item.price.toFixed(2)}
                                  </span>
                                )}
                              </>
                            )}
                            {isTiny && (
                              <span className="text-white font-bold text-[7px] font-mono" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
                                {item.displaySymbol?.substring(0, 3)}
                              </span>
                            )}
                            
                            {/* Hover tooltip */}
                            <div className="absolute hidden group-hover:block bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 pointer-events-none">
                              <div className="bg-[#0a1628]/95 backdrop-blur-sm border border-white/20 px-3 py-2 shadow-xl whitespace-nowrap">
                                <p className="text-secondary-500 text-[10px] font-mono uppercase">{item.category}</p>
                                <p className="text-white font-bold text-sm">{item.name}</p>
                                {item.price && <p className="text-white/60 text-xs font-mono">Price: ${item.price.toFixed(2)}</p>}
                                <p className={`text-xs font-mono font-bold ${item.changePercent >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                  {item.changePercent >= 0 ? '+' : ''}{item.changePercent?.toFixed(2)}%
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              )}

              {/* Legend - More Compact */}
              <div className="mt-4 flex flex-wrap items-center justify-center gap-3 text-[10px]">
                <span className="text-white/30 uppercase tracking-wider">Performance:</span>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-3" style={{ backgroundColor: '#c85353' }}></div>
                    <span className="text-white/50">-3%</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-3" style={{ backgroundColor: '#702828' }}></div>
                    <span className="text-white/50">-1%</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-3" style={{ backgroundColor: '#374151' }}></div>
                    <span className="text-white/50">0%</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-3" style={{ backgroundColor: '#007028' }}></div>
                    <span className="text-white/50">+1%</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-3" style={{ backgroundColor: '#00c853' }}></div>
                    <span className="text-white/50">+3%</span>
                  </div>
                </div>
                <span className="text-white/30 ml-2">| Tile size = magnitude of change</span>
              </div>
            </motion.div>
          )}

          {/* Calendar View */}
          {activeView === 'calendar' && (
            <motion.div
              key="calendar"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-4"
            >
              <div className="bg-white/5 border border-white/10">
                <div className="p-4 sm:p-6 border-b border-white/10">
                  <h2 className="text-xl font-heading font-bold text-white flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-secondary-500" />
                    Economic Calendar - Next 7 Days
                  </h2>
                  <p className="text-white/50 text-sm mt-1">High-impact events affecting commodity markets</p>
                </div>
                
                {loading ? (
                  <div className="p-6 space-y-4">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="h-16 bg-white/5 animate-pulse" />
                    ))}
                  </div>
                ) : economicEvents.length > 0 ? (
                  <div className="divide-y divide-white/10">
                    {economicEvents.map((event, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="p-4 sm:p-6 hover:bg-white/5 transition-colors"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                          <div className="flex items-center gap-4">
                            <div className="text-center min-w-[60px]">
                              <p className="text-secondary-500 text-xs font-mono uppercase">
                                {new Date(event.time || event.date).toLocaleDateString('en-US', { weekday: 'short' })}
                              </p>
                              <p className="text-white text-xl font-bold">
                                {new Date(event.time || event.date).getDate()}
                              </p>
                              <p className="text-white/40 text-xs">
                                {new Date(event.time || event.date).toLocaleDateString('en-US', { month: 'short' })}
                              </p>
                            </div>
                            <div className={`w-2 h-2 rounded-full ${
                              event.impact === 'high' ? 'bg-red-500' : 
                              event.impact === 'medium' ? 'bg-yellow-500' : 'bg-green-500'
                            }`} />
                          </div>
                          <div className="flex-1">
                            <h3 className="text-white font-medium">{event.event}</h3>
                            <p className="text-white/50 text-sm">
                              {event.country} • Impact: <span className={`capitalize ${
                                event.impact === 'high' ? 'text-red-400' : 
                                event.impact === 'medium' ? 'text-yellow-400' : 'text-green-400'
                              }`}>{event.impact}</span>
                            </p>
                          </div>
                          <div className="text-right">
                            {event.actual && (
                              <p className="text-white font-mono">Actual: {event.actual}</p>
                            )}
                            {event.estimate && (
                              <p className="text-white/50 text-sm font-mono">Est: {event.estimate}</p>
                            )}
                            {event.prev && (
                              <p className="text-white/40 text-xs font-mono">Prev: {event.prev}</p>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="p-12 text-center">
                    <Calendar className="w-12 h-12 text-white/20 mx-auto mb-4" />
                    <p className="text-white/50">No major commodity events scheduled</p>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* News View */}
          {activeView === 'news' && (
            <motion.div
              key="news"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <div className="grid md:grid-cols-2 gap-4">
                {loading ? (
                  [...Array(6)].map((_, i) => (
                    <div key={i} className="bg-white/5 h-48 animate-pulse" />
                  ))
                ) : marketNews.length > 0 ? (
                  marketNews.map((news, index) => (
                    <motion.a
                      key={index}
                      href={news.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="group bg-white/5 border border-white/10 hover:border-secondary-500/50 transition-all overflow-hidden"
                    >
                      <div className="flex flex-col sm:flex-row">
                        {news.image && (
                          <div className="w-full sm:w-48 h-32 sm:h-auto flex-shrink-0 overflow-hidden">
                            <img 
                              src={news.image} 
                              alt={news.headline}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          </div>
                        )}
                        <div className="p-4 flex flex-col justify-between flex-1">
                          <div>
                            <p className="text-secondary-500 text-xs font-mono uppercase mb-2">
                              {news.source} • {new Date(news.datetime * 1000).toLocaleDateString()}
                            </p>
                            <h3 className="text-white font-medium line-clamp-2 group-hover:text-secondary-400 transition-colors">
                              {news.headline}
                            </h3>
                            <p className="text-white/50 text-sm mt-2 line-clamp-2">
                              {news.summary}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 mt-4 text-secondary-500 text-xs">
                            Read more <ExternalLink className="w-3 h-3" />
                          </div>
                        </div>
                      </div>
                    </motion.a>
                  ))
                ) : (
                  <div className="col-span-2 p-12 text-center bg-white/5">
                    <Newspaper className="w-12 h-12 text-white/20 mx-auto mb-4" />
                    <p className="text-white/50">No commodity news available</p>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default MarketAnalysis;
